<template>
  <div class="sequence-editor">
    <div class="editor-content">
      <!-- 左侧面板 -->
      <div class="element-library">
        <div class="panel-header">
          <div class="panel-title">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="panel-icon">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span>时序图</span>
          </div>
          <button class="btn-history" @click="showHistoryDialog">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            历史时序图
          </button>
        </div>
        <div class="save-reminder">
          💡 提示：AI生成成功后会自动保存时序图。
        </div>

        <div class="ai-form-panel">
          <el-form :model="aiForm" label-position="top">
            <el-form-item label="课题名称">
              <el-input v-model="aiForm.projectName" placeholder="例如：在线购物系统" clearable />
            </el-form-item>
            <el-form-item label="业务流程名称">
              <el-input v-model="aiForm.moduleName" placeholder="例如：用户登录流程" clearable />
            </el-form-item>
            <el-form-item label="参与对象">
              <div class="actor-tags">
                <el-tag
                  v-for="(actor, index) in aiForm.actorList"
                  :key="index"
                  size="medium"
                  closable
                  @close="removeActor(index)"
                  style="margin: 0 4px 4px 0; font-size: 14px;"
                >{{ actor }}</el-tag>
                <div v-if="actorInputVisible" style="display:inline-block;">
                  <el-input
                    ref="actorInput"
                    v-model="actorInputValue"
                    size="small"
                    style="width: 100px;"
                    placeholder="输入对象名"
                    @keyup.enter.native="confirmActor"
                    @blur="confirmActor"
                  />
                </div>
                <el-button v-else type="primary" size="small" @click="showActorInput">+ 新增对象</el-button>
              </div>
              <div class="save-reminder" style="margin: 6px 0 0 0;padding: 1px 15px;">💡 有几个对象参与，就新增几个对象。</div>
            </el-form-item>
            <el-form-item label="系统业务描述">
              <textarea
                v-model="aiForm.functionDescription"
                class="func-editor"
                placeholder="请描述各对象之间的交互流程，例如：
用户登录流程：
- 用户：输入账号密码、点击登录
- 前端：校验输入、发送登录请求
- 后端：验证账号密码、返回Token
- 数据库：查询用户信息"
              />
            </el-form-item>
          </el-form>
        </div>

        <div class="panel-footer">
          <button class="btn-ai" @click="submitAIGenerate">
            <span class="btn-content">
              <img src="@/assets/ai-logo.png" alt="AI" class="ai-icon" />
              AI时序图
            </span>
            <span class="btn-points">（消耗{{ pointsConfig.sequenceDiagram }}积分）</span>
          </button>
        </div>
      </div>

      <!-- 右侧区域 -->
      <div class="canvas-area">
        <div class="tabs-container">
          <div class="tabs">
            <button class="tab active">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              时序图画布
            </button>
          </div>
          <div class="canvas-actions">
            <button class="tool-btn btn-green" @click="zoomIn" title="放大">
              <i class="el-icon-zoom-in"></i>放大
            </button>
            <button class="tool-btn btn-teal" @click="zoomOut" title="缩小">
              <i class="el-icon-zoom-out"></i>缩小
            </button>
            <button class="tool-btn btn-orange" @click="clearCanvas" title="清空画布">
              <i class="el-icon-delete"></i>清空画布
            </button>
            <button class="tool-btn btn-blue" @click="openExportDialog" title="导出时序图">
              <i class="el-icon-download"></i>导出时序图
            </button>
            <button class="tool-btn btn-purple" @click="toggleFullscreen" :title="isFullscreen ? '退出全屏' : '全屏查看'">
              <i class="el-icon-full-screen"></i>{{ isFullscreen ? '退出全屏' : '全屏' }}
            </button>
          </div>
        </div>

        <div class="toolbar">
          <div class="tip-banner">
            💡 提示：可导出为 Draw.io 文件进行进一步编辑和美化，支持全屏模式获得更大编辑空间。
          </div>
        </div>

        <!-- 参数设置 -->
        <div class="params-setting">
          <div class="param-item">
            <label>字体:</label>
            <el-select v-model="fontFamily" size="small" style="width: 100px;" @change="updateFont">
              <el-option label="宋体" value="SimSun"></el-option>
              <el-option label="微软雅黑" value="Microsoft YaHei"></el-option>
              <el-option label="黑体" value="SimHei"></el-option>
              <el-option label="仿宋" value="FangSong"></el-option>
              <el-option label="楷体" value="KaiTi"></el-option>
              <el-option label="Arial" value="Arial"></el-option>
            </el-select>
          </div>
          <div class="param-item">
            <label>字体大小:</label>
            <el-select v-model="fontSize" size="small" style="width: 80px;" @change="updateFont">
              <el-option label="10px" :value="10"></el-option>
              <el-option label="12px" :value="12"></el-option>
              <el-option label="14px" :value="14"></el-option>
              <el-option label="16px" :value="16"></el-option>
              <el-option label="18px" :value="18"></el-option>
              <el-option label="20px" :value="20"></el-option>
              <el-option label="22px" :value="22"></el-option>
            </el-select>
          </div>
          <div class="param-item">
            <label>字体加粗:</label>
            <el-select v-model="fontWeight" size="small" style="width: 80px;" @change="updateFont">
              <el-option label="正常" value="normal"></el-option>
              <el-option label="加粗" value="bold"></el-option>
            </el-select>
          </div>

          <div class="param-item">
            <label>线条宽度:</label>
            <div class="number-input">
              <el-button size="small" @click="decreaseLineWidth">-</el-button>
              <span class="number-value">{{ lineWidth }}</span>
              <el-button size="small" @click="increaseLineWidth">+</el-button>
            </div>
          </div>
          <div class="param-item">
            <label>箭头尺寸:</label>
            <el-select v-model="arrowSize" size="small" style="width: 80px;" @change="updateArrowSize">
              <el-option label="无" value="none"></el-option>
              <el-option label="小" value="small"></el-option>
              <el-option label="中" value="medium"></el-option>
              <el-option label="大" value="large"></el-option>
            </el-select>
          </div>
        </div>

        <!-- 时序图画布 -->
        <div class="canvas-content">
          <div class="canvas" ref="canvasWrap"
            @mousedown="onCanvasMouseDown"
            @mousemove="onCanvasMouseMove"
            @mouseup="onCanvasMouseUp"
            @mouseleave="onCanvasMouseUp"
            @wheel.prevent="onWheel"
          >
            <svg ref="seqSvg" class="seq-svg"
              :width="svgWidth" :height="svgHeight"
            :style="{ transform: `scale(${zoom}) translate(${panX}px,${panY}px)`, transformOrigin: '0 0' }"
          >
            <defs>
              <!-- 实线箭头 -->
              <marker id="seq-arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#333"/>
              </marker>
              <!-- 虚线箭头 -->
              <marker id="seq-arrow-dash" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#333"/>
              </marker>
            </defs>

            <!-- alt 框 -->
            <g v-for="frame in seqData.frames" :key="frame.id">
              <rect :x="frame.x" :y="frame.y" :width="frame.w" :height="frame.h"
                fill="none" :stroke="lineColor" stroke-width="1.5" stroke-dasharray="6,3"/>
              <!-- 左上角标签五边形 -->
              <polygon :points="`${frame.x},${frame.y} ${frame.x+40},${frame.y} ${frame.x+40},${frame.y+20} ${frame.x+32},${frame.y+28} ${frame.x},${frame.y+28}`"
                fill="#eee" :stroke="lineColor" stroke-width="1.5"/>
              <text :x="frame.x+8" :y="frame.y+18" :font-size="fontSize" :fill="fontColor" :font-family="fontFamily" font-style="italic">{{ frame.label }}</text>
              <!-- 分隔线 -->
              <line v-for="(sep, si) in frame.separators" :key="si"
                :x1="frame.x" :y1="sep.y" :x2="frame.x+frame.w" :y2="sep.y"
                :stroke="lineColor" stroke-width="1" stroke-dasharray="6,3"/>
              <!-- 分隔条件文字 -->
              <text v-for="(sep, si) in frame.separators" :key="'st'+si"
                :x="frame.x+8" :y="sep.y+16" :font-size="fontSize" :fill="fontColor" :font-family="fontFamily">[{{ sep.label }}]</text>
              <!-- 顶部条件文字 -->
              <text :x="frame.x+50" :y="frame.y+16" :font-size="fontSize" :fill="fontColor" :font-family="fontFamily">[{{ frame.topLabel }}]</text>
            </g>

            <!-- 生命线虚线 -->
            <g v-for="p in seqData.participants" :key="'line-'+p.id">
              <line :x1="p.cx" :y1="p.headBottom" :x2="p.cx" :y2="svgHeight - 20"
                :stroke="lineColor" :stroke-width="lineWidth" stroke-dasharray="6,4"/>
            </g>

            <!-- 激活条 -->
            <g v-for="p in seqData.participants" :key="'act-'+p.id">
              <rect v-for="(act, ai) in p.activations" :key="ai"
                :x="p.cx - 5" :y="act.y" width="10" :height="act.h"
                fill="#eee" :stroke="lineColor" :stroke-width="lineWidth"/>
            </g>

            <!-- 消息箭头 -->
            <g v-for="msg in seqData.messages" :key="msg.id">
              <!-- 自调用弧线 -->
              <path v-if="msg.self"
                :d="`M ${msg.x1} ${msg.y} C ${msg.x1+50} ${msg.y}, ${msg.x1+50} ${msg.y+(msg.selfH||25)}, ${msg.x1} ${msg.y+(msg.selfH||25)}`"
                fill="none" :stroke="lineColor" :stroke-width="lineWidth" marker-end="url(#seq-arrow)"/>
              <!-- 自调用标签（双击编辑） -->
              <text v-if="msg.self && editingMsg !== msg.id"
                :x="msg.x1 + 35" :y="msg.y + (msg.selfH||25)/2 + 5"
                text-anchor="start" :font-size="fontSize" :fill="fontColor" :font-family="fontFamily" :font-weight="fontWeight"
                style="cursor:pointer" @dblclick.stop="startEditMsg(msg)">{{ msg.label }}</text>
              <!-- 普通消息线 -->
              <line v-else-if="!msg.self"
                :x1="msg.x1" :y1="msg.y" :x2="msg.x2" :y2="msg.y"
                :stroke="lineColor" :stroke-width="lineWidth"
                :stroke-dasharray="msg.dashed ? '6,4' : 'none'"
                :marker-end="msg.noArrow ? '' : 'url(#seq-arrow)'"/>
              <!-- 普通消息标签（双击编辑） -->
              <text v-if="!msg.self && editingMsg !== msg.id"
                :x="(msg.x1+msg.x2)/2" :y="msg.y - 6"
                text-anchor="middle" :font-size="fontSize" :fill="fontColor" :font-family="fontFamily" :font-weight="fontWeight"
                style="cursor:pointer" @dblclick.stop="startEditMsg(msg)">{{ msg.label }}</text>
              <!-- 编辑输入框 -->
              <foreignObject v-if="editingMsg === msg.id"
                :x="msg.self ? msg.x1+55 : (msg.x1+msg.x2)/2 - 80"
                :y="msg.self ? msg.y+(msg.selfH||25)/2-10 : msg.y-22"
                width="160" height="26">
                <input
                  :ref="'msgInput_'+msg.id"
                  v-model="editingText"
                  @keydown.enter="saveEditMsg(msg)"
                  @keydown.esc="cancelEditMsg"
                  @blur="saveEditMsg(msg)"
                  style="width:100%;height:24px;border:2px solid #1890ff;border-radius:3px;padding:0 6px;font-size:12px;outline:none;box-sizing:border-box;"
                />
              </foreignObject>
            </g>

            <!-- 参与者头部（最后渲染，覆盖在生命线上方） -->
            <g v-for="p in seqData.participants" :key="'head-'+p.id">
              <!-- Actor 小人 -->
              <g v-if="p.isActor">
                <circle :cx="p.cx" :cy="p.y + 12" r="10" fill="none" :stroke="lineColor" :stroke-width="lineWidth"/>
                <line :x1="p.cx" :y1="p.y+22" :x2="p.cx" :y2="p.y+48" :stroke="lineColor" :stroke-width="lineWidth"/>
                <line :x1="p.cx-14" :y1="p.y+34" :x2="p.cx+14" :y2="p.y+34" :stroke="lineColor" :stroke-width="lineWidth"/>
                <line :x1="p.cx" :y1="p.y+48" :x2="p.cx-10" :y2="p.y+62" :stroke="lineColor" :stroke-width="lineWidth"/>
                <line :x1="p.cx" :y1="p.y+48" :x2="p.cx+10" :y2="p.y+62" :stroke="lineColor" :stroke-width="lineWidth"/>
                <!-- Actor 名字（双击编辑） -->
                <text v-if="editingParticipant !== p.id"
                  :x="p.cx" :y="p.y+78" text-anchor="middle"
                  :font-size="fontSize" :fill="fontColor" :font-family="fontFamily" :font-weight="fontWeight"
                  style="cursor:pointer" @dblclick.stop="startEditParticipant(p)">{{ p.name }}</text>
                <foreignObject v-else :x="p.cx-60" :y="p.y+62" width="120" height="26">
                  <input :ref="'pInput_'+p.id" v-model="editingText"
                    @keydown.enter="saveEditParticipant(p)" @keydown.esc="cancelEditParticipant" @blur="saveEditParticipant(p)"
                    style="width:100%;height:24px;border:2px solid #1890ff;border-radius:3px;padding:0 6px;font-size:12px;outline:none;box-sizing:border-box;text-align:center;"/>
                </foreignObject>
              </g>
              <!-- 普通矩形头部 -->
              <g v-else>
                <rect :x="p.x" :y="p.y" :width="p.w" :height="p.headH"
                  fill="#eee" :stroke="lineColor" :stroke-width="lineWidth"/>
                <!-- 参与者名字（双击编辑） -->
                <text v-if="editingParticipant !== p.id"
                  :x="p.cx" :y="p.y + p.headH/2 + 5" text-anchor="middle"
                  :font-size="fontSize" :fill="fontColor" :font-family="fontFamily" :font-weight="fontWeight"
                  style="cursor:pointer" @dblclick.stop="startEditParticipant(p)">{{ p.name }}</text>
                <foreignObject v-else :x="p.x+4" :y="p.y+8" :width="p.w-8" height="26">
                  <input :ref="'pInput_'+p.id" v-model="editingText"
                    @keydown.enter="saveEditParticipant(p)" @keydown.esc="cancelEditParticipant" @blur="saveEditParticipant(p)"
                    style="width:100%;height:24px;border:2px solid #1890ff;border-radius:3px;padding:0 6px;font-size:12px;outline:none;box-sizing:border-box;text-align:center;"/>
                </foreignObject>
              </g>
            </g>
          </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- 系统业务描述查看弹框 -->
    <el-dialog title="系统业务描述" :visible.sync="showDescDialog" width="500px">
      <div style="white-space: pre-wrap; line-height: 1.8; font-size: 14px; color: #333; height: 300px; overflow-y: auto; padding: 4px 8px;">{{ currentDesc }}</div>
      <span slot="footer">
        <el-button @click="showDescDialog = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 历史时序图弹框 -->
    <el-dialog
      title="历史时序图"
      :visible.sync="showHistoryModal"
      width="1000px"
      :before-close="closeHistoryDialog"
    >
      <div class="history-content">
        <div class="history-description" style="margin-bottom: 12px;">
          <ul>
            <li>• 共有 {{ savedDiagrams.length }} 个已保存的时序图（按保存时间倒序排列）</li>
            <li>• 点击"加载"按钮可以加载对应的时序图</li>
            <li>• 系统最多保存20条历史时序图记录</li>
          </ul>
        </div>

        <el-table
          :data="reversedSavedDiagrams"
          stripe
          style="width: 100%"
          :row-class-name="getRowClassName"
          empty-text="暂无保存的历史时序图"
        >
          <el-table-column type="index" label="序号" width="55" align="center" />
          <el-table-column label="课题名称" min-width="120" align="center">
            <template slot-scope="scope">
              <span class="diagram-name" :class="{ current: currentDiagramId === scope.row.id }">
                {{ scope.row.projectName || '-' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="业务模块" min-width="120" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.moduleName || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="参与对象" width="150" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.actors || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="系统业务描述" width="130" align="center">
            <template slot-scope="scope">
              <el-button
                v-if="scope.row.functionDescription"
                size="mini"
                type="text"
                @click="viewFunctionDescription(scope.row.functionDescription)"
              >查看</el-button>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="保存时间" width="160" align="center">
            <template slot-scope="scope">
              <span class="absolute-time">{{ formatAbsoluteTime(scope.row.timestamp) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="140" align="center">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button size="mini" type="primary" @click="loadDiagramFromHistory(scope.row)">加载</el-button>
                <el-button size="mini" type="danger" @click="deleteDiagramFromHistory(getOriginalIndex(scope.row))">删除</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            :current-page="currentPage"
            :page-size="pageSize"
            :total="savedDiagrams.length"
            @current-change="handlePageChange"
            layout="prev, pager, next"
          />
        </div>
      </div>
    </el-dialog>

    <!-- 导出对话框 -->
    <ExportDialog
      :visible.sync="isExportDialogVisible"
      @confirm="handleExportConfirm"
    />
  </div>
</template>

<script>
import ExportDialog from '@/components/ExportDialog.vue'

export default {
  name: 'Sequence',
  components: { ExportDialog },
  data() {
    return {
      // 积分配置
      pointsConfig: {
        sequenceDiagram: 0
      },

      // 参数设置
      fontFamily: 'SimSun',
      fontSize: 18,
      fontWeight: 'normal',
      fontColor: '#333333',
      lineColor: '#333333',
      lineWidth: 1,
      arrowSize: 'medium',

      // 全屏状态
      isFullscreen: false,

      // 保存管理
      savedDiagrams: [],
      currentDiagramId: null,
      showHistoryModal: false,
      showDescDialog: false,
      currentDesc: '',

      // 分页
      currentPage: 1,
      pageSize: 10,

      // 表单
      aiForm: {
        projectName: '',
        moduleName: '',
        actorList: [],
        functionDescription: ''
      },
      actorInputVisible: false,
      actorInputValue: '',

      // 内联编辑
      editingMsg: null,
      editingParticipant: null,
      editingText: '',

      // 导出对话框
      isExportDialogVisible: false,

      // 画布缩放平移
      zoom: 1,
      panX: 0,
      panY: 0,
      isPanning: false,
      panStart: { x: 0, y: 0 },
      panOrigin: { x: 0, y: 0 },
      svgWidth: 900,
      svgHeight: 820,

      // 时序图静态数据（示例：用户登录流程）
      seqData: {
        participants: [],
        messages: [],
        frames: []
      }
    }
  },
  computed: {
    reversedSavedDiagrams() {
      const reversed = [...this.savedDiagrams].reverse();
      const start = (this.currentPage - 1) * this.pageSize;
      return reversed.slice(start, start + this.pageSize);
    }
  },
  methods: {
    // 加载积分配置
    loadPointsConfig() {
      this.request.get('/ai/points-config').then(res => {
        if (res.code === '200' && res.data) {
          this.pointsConfig.sequenceDiagram = res.data.sequenceDiagram || 0;
        }
      }).catch(() => {});
    },

    increasePageView() {
      this.request.get('/pageView/increase', {
        params: { pageName: 'sequence' }
      }).catch(() => {})
    },
    // 角色管理
    showActorInput() {
      this.actorInputVisible = true;
      this.$nextTick(() => {
        this.$refs.actorInput && this.$refs.actorInput.focus();
      });
    },
    confirmActor() {
      const val = this.actorInputValue.trim();
      if (val && !this.aiForm.actorList.includes(val)) {
        this.aiForm.actorList.push(val);
      }
      this.actorInputVisible = false;
      this.actorInputValue = '';
    },
    removeActor(index) {
      this.aiForm.actorList.splice(index, 1);
    },

    // 消息文字编辑
    startEditMsg(msg) {
      this.editingMsg = msg.id;
      this.editingText = msg.label;
      this.$nextTick(() => {
        const el = this.$refs['msgInput_' + msg.id];
        const input = Array.isArray(el) ? el[0] : el;
        input && input.focus && input.focus();
      });
    },
    saveEditMsg(msg) {
      if (this.editingMsg !== msg.id) return;
      msg.label = this.editingText;
      this.editingMsg = null;
      this.editingText = '';
    },
    cancelEditMsg() {
      this.editingMsg = null;
      this.editingText = '';
    },

    // 参与者名字编辑
    startEditParticipant(p) {
      this.editingParticipant = p.id;
      this.editingText = p.name;
      this.$nextTick(() => {
        const el = this.$refs['pInput_' + p.id];
        const input = Array.isArray(el) ? el[0] : el;
        input && input.focus && input.focus();
      });
    },
    saveEditParticipant(p) {
      if (this.editingParticipant !== p.id) return;
      p.name = this.editingText;
      this.editingParticipant = null;
      this.editingText = '';
    },
    cancelEditParticipant() {
      this.editingParticipant = null;
      this.editingText = '';
    },

    // 参数更新
    updateFont() {},
    updateLineStyle() {},
    increaseLineWidth() {
      if (this.lineWidth < 5) this.lineWidth += 1;
    },
    decreaseLineWidth() {
      if (this.lineWidth > 1) this.lineWidth -= 1;
    },
    updateArrowSize() {},

    // 画布操作
    zoomIn() { this.zoom = Math.min(2, +(this.zoom + 0.1).toFixed(1)); },
    zoomOut() { this.zoom = Math.max(0.3, +(this.zoom - 0.1).toFixed(1)); },
    clearCanvas() {
      this.$confirm('确定清空当前时序图？', '提示', { type: 'warning' })
        .then(() => { this.seqData = { participants: [], messages: [], frames: [] }; })
        .catch(() => {});
    },
    openExportDialog() { this.isExportDialogVisible = true; },
    handleExportConfirm(options) {
      if (options.format === 'drawio') {
        this.exportToDrawio();
      } else {
        this.downloadImage(options.background);
      }
    },

    // 导出为 Draw.io 时序图格式
    exportToDrawio() {
      try {
        const cells = [];
        let id = 2;
        const fs = this.fontSize;
        const ff = this.fontFamily;
        const lc = this.lineColor.replace('#', '');
        const lw = this.lineWidth;
        const idMap = {};      // participantId -> lifeline cellId
        const actIdMap = {};   // participantId -> activation cellId (第一条激活条)

        const totalH = this.svgHeight;
        const lifeY  = 20;
        const lifeH  = totalH - lifeY;

        // ── 1. 生命线 + 激活条 ──────────────────────────────────────────
        this.seqData.participants.forEach(p => {
          const lifeId = id++;
          idMap[p.id] = lifeId;
          const x = Math.round(p.cx - p.w / 2);
          const style =
            `shape=umlLifeline;perimeter=lifelinePerimeter;whiteSpace=wrap;` +
            `container=1;dropTarget=0;collapsible=0;recursiveResize=0;outlineConnect=0;` +
            `portConstraint=eastwest;fontSize=${fs};fontFamily=${ff};` +
            `strokeColor=#${lc};strokeWidth=${lw};fillColor=#eeeeee;size=${p.headH};`;
          cells.push(
            `<mxCell id="${lifeId}" value="${this.escapeXml(p.name)}" vertex="1" parent="1" style="${style}">` +
            `<mxGeometry x="${x}" y="${lifeY}" width="${p.w}" height="${lifeH}" as="geometry"/>` +
            `</mxCell>`
          );

          // 激活条作为生命线的子 cell
          if (p.activations && p.activations.length > 0) {
            p.activations.forEach((act, ai) => {
              const actId = id++;
              if (ai === 0) actIdMap[p.id] = actId;
              // 激活条 y 相对于生命线容器
              const relY = Math.round(act.y - lifeY);
              const actStyle =
                `points=[];perimeter=orthogonalPerimeter;outlineConnect=0;` +
                `targetShapes=umlLifeline;portConstraint=eastwest;` +
                `fillColor=#eeeeee;strokeColor=#${lc};strokeWidth=${lw};`;
              cells.push(
                `<mxCell id="${actId}" value="" vertex="1" parent="${lifeId}" style="${actStyle}">` +
                `<mxGeometry x="${Math.round(p.w / 2 - 5)}" y="${relY}" width="10" height="${Math.round(act.h)}" as="geometry"/>` +
                `</mxCell>`
              );
            });
          }
        });

        // ── 2. 消息箭头 ─────────────────────────────────────────────────
        this.seqData.messages.forEach(msg => {
          const edgeId = id++;
          const isDashed = !!msg.dashed;
          const isSelf   = !!msg.self;

          const srcP = this.seqData.participants.find(p => Math.abs(p.cx - msg.x1) < 15);
          const tgtP = this.seqData.participants.find(p => Math.abs(p.cx - msg.x2) < 15);
          const srcCellId = srcP ? idMap[srcP.id] : null;
          const tgtCellId = tgtP ? idMap[tgtP.id] : null;

          // Y 比例（相对于生命线容器）
          const yRatio = (y) => parseFloat(Math.max(0, Math.min(1, (y - lifeY) / lifeH)).toFixed(4));

          if (isSelf) {
            // 自调用：sourcePoint/targetPoint 精确在激活条右边缘
            const x1 = Math.round(msg.x1); // msg.x1 已经是 cx + AW（激活条右边缘）
            const y1 = Math.round(msg.y);
            const y2 = Math.round(msg.y + (msg.selfH || 35));
            const xOut = x1 + 50;
            const style =
              `curved=1;endArrow=block;endFill=1;rounded=0;` +
              `fontSize=${fs};fontFamily=${ff};strokeColor=#${lc};strokeWidth=${lw};` +
              `verticalAlign=bottom;align=left;` +
              (isDashed ? 'dashed=1;dashPattern=2 3;' : '');
            cells.push(
              `<mxCell id="${edgeId}" value="${this.escapeXml(msg.label)}" edge="1" parent="1" style="${style}">` +
              `<mxGeometry relative="1" as="geometry">` +
              `<Array as="points">` +
              `<mxPoint x="${xOut}" y="${y1}"/>` +
              `<mxPoint x="${xOut}" y="${y2}"/>` +
              `</Array>` +
              `<mxPoint x="${x1}" y="${y1}" as="sourcePoint"/>` +
              `<mxPoint x="${x1}" y="${y2}" as="targetPoint"/>` +
              `</mxGeometry></mxCell>`
            );
          } else {
            // 普通消息：用绝对坐标精确连接激活条边缘
            const AW = 5; // 激活条半宽
            const x1 = Math.round(msg.x1); // 已经是激活条边缘坐标
            const x2 = Math.round(msg.x2);
            const y  = Math.round(msg.y);
            const style =
              `endArrow=block;endFill=1;rounded=0;` +
              `fontSize=${fs};fontFamily=${ff};strokeColor=#${lc};strokeWidth=${lw};` +
              `verticalAlign=bottom;align=center;` +
              (isDashed ? 'dashed=1;dashPattern=2 3;' : '');
            cells.push(
              `<mxCell id="${edgeId}" value="${this.escapeXml(msg.label)}" edge="1" parent="1" style="${style}">` +
              `<mxGeometry relative="1" as="geometry">` +
              `<mxPoint x="${x1}" y="${y}" as="sourcePoint"/>` +
              `<mxPoint x="${x2}" y="${y}" as="targetPoint"/>` +
              `</mxGeometry></mxCell>`
            );
          }
        });

        // ── 3. alt 框 + 分隔线 ──────────────────────────────────────────
        this.seqData.frames.forEach(frame => {
          const frameId = id++;
          // umlFrame: value 写 "alt"，左上角五边形自动显示
          cells.push(
            `<mxCell id="${frameId}" value="${this.escapeXml(frame.label)}" vertex="1" parent="1"` +
            ` style="shape=umlFrame;dashed=1;pointerEvents=0;dropTarget=0;` +
            `strokeColor=#B3B3B3;fillColor=#eeeeee;fontStyle=2;height=20;width=30;fontSize=${fs};fontFamily=${ff};">` +
            `<mxGeometry x="${Math.round(frame.x)}" y="${Math.round(frame.y)}" width="${Math.round(frame.w)}" height="${Math.round(frame.h)}" as="geometry"/>` +
            `</mxCell>`
          );
          // 顶部条件文字（[验证成功]）单独 text cell
          cells.push(
            `<mxCell id="${id++}" value="${this.escapeXml('[' + frame.topLabel + ']')}" vertex="1" parent="1"` +
            ` style="text;strokeColor=none;fillColor=none;align=left;verticalAlign=middle;` +
            `whiteSpace=wrap;fontSize=${fs};fontFamily=${ff};">` +
            `<mxGeometry x="${Math.round(frame.x + 42)}" y="${Math.round(frame.y)}" width="${Math.round(frame.w - 42)}" height="20" as="geometry"/>` +
            `</mxCell>`
          );
          // 分隔线 + 条件文字
          (frame.separators || []).forEach(sep => {
            cells.push(
              `<mxCell id="${id++}" value="" vertex="1" parent="1"` +
              ` style="shape=line;dashed=1;whiteSpace=wrap;strokeColor=#B3B3B3;">` +
              `<mxGeometry x="${Math.round(frame.x)}" y="${Math.round(sep.y)}" width="${Math.round(frame.w)}" height="4" as="geometry"/>` +
              `</mxCell>`
            );
            cells.push(
              `<mxCell id="${id++}" value="${this.escapeXml('[' + sep.label + ']')}" vertex="1" parent="1"` +
              ` style="text;strokeColor=none;fillColor=none;align=left;verticalAlign=middle;` +
              `whiteSpace=wrap;fontSize=${fs};fontFamily=${ff};">` +
              `<mxGeometry x="${Math.round(frame.x + 4)}" y="${Math.round(sep.y + 4)}" width="${Math.round(frame.w - 8)}" height="20" as="geometry"/>` +
              `</mxCell>`
            );
          });
        });

        const xml =
`<?xml version="1.0" encoding="UTF-8"?>
<mxfile host="app.diagrams.net" modified="${new Date().toISOString()}" version="21.0.0" type="device">
  <diagram name="时序图" id="sequence-diagram">
    <mxGraphModel dx="1422" dy="794" grid="1" gridSize="10" guides="1" tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" pageWidth="1169" pageHeight="827" math="0" shadow="0">
      <root>
        <mxCell id="0"/>
        <mxCell id="1" parent="0"/>
        ${cells.join('\n        ')}
      </root>
    </mxGraphModel>
  </diagram>
</mxfile>`;

        const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = '时序图_' + new Date().toISOString().slice(0,19).replace(/[:-]/g,'') + '.drawio';
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        this.$message.success('Draw.io 文件导出成功');
      } catch (e) {
        console.error(e);
        this.$message.error('导出失败，请重试');
      }
    },

    // 导出为 PNG
    downloadImage(background = 'white') {
      const svgEl = this.$refs.seqSvg;
      if (!svgEl) { this.$message.error('未找到时序图内容'); return; }

      const padding = 30;
      const w = this.svgWidth + padding * 2;
      const h = this.svgHeight + padding * 2;

      // 克隆 SVG，去掉 transform（缩放/平移），设置正确的 viewBox 和尺寸
      const clone = svgEl.cloneNode(true);
      clone.removeAttribute('style');
      clone.setAttribute('width', w);
      clone.setAttribute('height', h);
      clone.setAttribute('viewBox', `${-padding} ${-padding} ${w} ${h}`);
      clone.style.transform = '';

      let svgData = new XMLSerializer().serializeToString(clone);
      if (!svgData.includes('xmlns=')) svgData = svgData.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      if (!svgData.includes('xmlns:xlink=')) svgData = svgData.replace('<svg', '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');

      const pixelRatio = 3;
      const canvas = document.createElement('canvas');
      canvas.width = w * pixelRatio;
      canvas.height = h * pixelRatio;
      const ctx = canvas.getContext('2d');
      ctx.scale(pixelRatio, pixelRatio);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      if (background !== 'transparent') { ctx.fillStyle = background; ctx.fillRect(0, 0, w, h); }

      const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        const link = document.createElement('a');
        link.download = '时序图_' + new Date().toISOString().slice(0,19).replace(/[:-]/g,'') + '.png';
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
        this.$message.success('图片导出成功');
      };
      img.onerror = () => { URL.revokeObjectURL(url); this.$message.error('导出失败，请重试'); };
      img.src = url;
    },

    // XML 转义
    escapeXml(text) {
      if (!text) return '';
      return String(text).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&apos;');
    },
    toggleFullscreen() {
      const el = this.$el.querySelector('.canvas-area');
      if (!this.isFullscreen) {
        const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
        if (req) req.call(el);
      } else {
        const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
        if (exit) exit.call(document);
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
    },

    // 画布拖拽平移
    onCanvasMouseDown(e) {
      if (e.button !== 0) return;
      this.isPanning = true;
      this.panStart = { x: e.clientX, y: e.clientY };
      this.panOrigin = { x: this.panX, y: this.panY };
    },
    onCanvasMouseMove(e) {
      if (!this.isPanning) return;
      this.panX = this.panOrigin.x + (e.clientX - this.panStart.x) / this.zoom;
      this.panY = this.panOrigin.y + (e.clientY - this.panStart.y) / this.zoom;
    },
    onCanvasMouseUp() { this.isPanning = false; },
    onWheel(e) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.zoom = Math.min(2, Math.max(0.3, +(this.zoom + delta).toFixed(1)));
    },

    // 构建示例时序图数据
    buildDefaultSeqData() {
      // ── 布局常量（对照图2精确还原）──
      const TOP    = 20;   // 顶部留白
      const HEAD_H = 40;   // 矩形头部高度
      const ACTOR_H = 75;  // Actor小人+名字总高度
      const COL_W  = 200;  // 列间距
      const P_W    = 110;  // 矩形宽度
      const AW     = 5;    // 激活条半宽

      // 列中心 x
      const CX = [60, 60 + COL_W, 60 + COL_W*2, 60 + COL_W*3];

      // 参与者定义
      const participants = [
        { id: 'user', name: '用户',     cx: CX[0], isActor: false,
          x: CX[0]-P_W/2, y: TOP, w: P_W, headH: HEAD_H,
          headBottom: TOP + HEAD_H, activations: [] },
        { id: 'fe',   name: '前端',     cx: CX[1], isActor: false,
          x: CX[1]-P_W/2, y: TOP, w: P_W, headH: HEAD_H,
          headBottom: TOP + HEAD_H, activations: [] },
        { id: 'auth', name: '认证服务', cx: CX[2], isActor: false,
          x: CX[2]-P_W/2, y: TOP, w: P_W, headH: HEAD_H,
          headBottom: TOP + HEAD_H, activations: [] },
        { id: 'db',   name: '数据库',   cx: CX[3], isActor: false,
          x: CX[3]-P_W/2, y: TOP, w: P_W, headH: HEAD_H,
          headBottom: TOP + HEAD_H, activations: [] },
      ];

      // ── Y 轴时间线（严格按图2顺序）──
      // 消息行 Y 坐标
      const Y = {
        m1:  120,  // 输入用户名/密码
        m2s: 160,  // 前端基础校验 自调用 start
        m2e: 190,  // 前端基础校验 自调用 end
        m3:  220,  // POST /api/login
        m4:  270,  // 查询用户信息
        m5:  320,  // 返回用户数据
        m6s: 360,  // 验证密码 自调用 start
        m6e: 395,  // 验证密码 自调用 end
        // alt 框从这里开始
        altTop: 415,
        m7s: 460,  // 生成JWT Token 自调用 start（在alt内）
        m7e: 495,  // 生成JWT Token 自调用 end
        m8:  530,  // 返回Token及用户信息
        m9:  570,  // 登录成功，跳转首页
        altSep: 615, // alt 分隔线
        m10: 650,  // 返回错误
        m11: 690,  // 显示错误提示
        altBot: 730,
      };

      // ── 激活条 ──
      // 前端：从 m1 到 m3 结束（第一段），m8~m9（第二段），m10~m11（第三段）
      participants[1].activations = [
        { y: Y.m1,  h: Y.m3 + 30 - Y.m1 },   // 接收输入 → 发出login请求
        { y: Y.m8,  h: Y.m9 + 20 - Y.m8 },   // 接收token → 跳转
        { y: Y.m10, h: Y.m11 + 20 - Y.m10 }, // 接收错误 → 显示提示
      ];
      // 认证服务：从 m3 到 m8
      participants[2].activations = [
        { y: Y.m3, h: Y.m8 - Y.m3 },
      ];
      // 数据库：从 m4 到 m5
      participants[3].activations = [
        { y: Y.m4, h: Y.m5 + 10 - Y.m4 },
      ];

      const cx = (id) => participants.find(p => p.id === id).cx;

      // ── 消息列表 ──
      const messages = [
        // 1. 用户 → 前端（实线）
        { id: 1,  x1: cx('user'),      x2: cx('fe') - AW,   y: Y.m1,  label: '输入用户名/密码',              dashed: false },
        // 2. 前端自调用（小弧）
        { id: 2,  x1: cx('fe') + AW,   x2: cx('fe') + AW,   y: Y.m2s, label: '前端基础校验',                 dashed: false, self: true, selfH: Y.m2e - Y.m2s },
        // 3. 前端 → 认证服务（实线，箭头向右，标签在线上方）
        { id: 3,  x1: cx('fe') + AW,   x2: cx('auth') - AW, y: Y.m3,  label: 'POST /api/login',              dashed: false },
        // 4. 认证服务 → 数据库（实线）
        { id: 4,  x1: cx('auth') + AW, x2: cx('db') - AW,   y: Y.m4,  label: '查询用户信息',                 dashed: false },
        // 5. 数据库 → 认证服务（虚线返回）
        { id: 5,  x1: cx('db') - AW,   x2: cx('auth') + AW, y: Y.m5,  label: '返回用户数据',                 dashed: true  },
        // 6. 认证服务自调用（验证密码）
        { id: 6,  x1: cx('auth') + AW, x2: cx('auth') + AW, y: Y.m6s, label: '验证密码',                     dashed: false, self: true, selfH: Y.m6e - Y.m6s },
        // 7. 认证服务自调用（生成JWT Token，在alt框内）
        { id: 7,  x1: cx('auth') + AW, x2: cx('auth') + AW, y: Y.m7s, label: '生成JWT Token',                dashed: false, self: true, selfH: Y.m7e - Y.m7s },
        // 8. 认证服务 → 前端（虚线返回）
        { id: 8,  x1: cx('auth') - AW, x2: cx('fe') + AW,   y: Y.m8,  label: '返回Token及用户信息',          dashed: true  },
        // 9. 前端 → 用户（虚线）
        { id: 9,  x1: cx('fe') - AW,   x2: cx('user'),       y: Y.m9,  label: '登录成功，跳转首页',           dashed: true  },
        // 10. 认证服务 → 前端（虚线，验证失败分支）
        { id: 10, x1: cx('auth') - AW, x2: cx('fe') + AW,   y: Y.m10, label: '返回错误（用户名或密码错误）', dashed: true  },
        // 11. 前端 → 用户（虚线）
        { id: 11, x1: cx('fe') - AW,   x2: cx('user'),       y: Y.m11, label: '显示错误提示',                 dashed: true  },
      ];

      // ── alt 框 ──
      // 左边紧贴用户生命线左侧，右边到数据库右侧
      const altX = cx('user') - 35;
      const altW = cx('db') + P_W/2 + 10 - altX;
      const frames = [
        {
          id: 'alt1',
          label: 'alt',
          topLabel: '验证成功',
          x: altX,
          y: Y.altTop,
          w: altW,
          h: Y.altBot - Y.altTop,
          separators: [{ y: Y.altSep, label: '验证失败' }]
        }
      ];

      this.seqData = { participants, messages, frames };
      this.svgWidth  = CX[3] + P_W/2 + 60;
      this.svgHeight = Y.altBot + 40;
    },

    // 保存
    saveCurrentDiagram() {
      if (!this.aiForm.projectName.trim()) {
        this.$message.warning('请填写课题名称');
        return;
      }
      if (!this.aiForm.moduleName.trim()) {
        this.$message.warning('请填写业务流程名称');
        return;
      }

      if (!this.aiForm.functionDescription.trim()) {
        this.$message.warning('请填写系统业务描述');
        return;
      }
      const diagramData = {
        id: 'seq_' + Date.now(),
        projectName: this.aiForm.projectName,
        moduleName: this.aiForm.moduleName,
        actors: this.aiForm.actorList.join(','),
        functionDescription: this.aiForm.functionDescription,
        timestamp: Date.now(),
        seqData: JSON.parse(JSON.stringify(this.seqData)),
        svgWidth: this.svgWidth,
        svgHeight: this.svgHeight,
        settings: {
          fontFamily: this.fontFamily,
          fontSize: this.fontSize,
          fontWeight: this.fontWeight,
          fontColor: this.fontColor,
          lineColor: this.lineColor,
          lineWidth: this.lineWidth,
          arrowSize: this.arrowSize
        }
      };
      let saved = this.loadSavedDiagrams();
      saved.push(diagramData);
      if (saved.length > 20) {
        const removed = saved.shift();
        this.$message.info('已删除最早的时序图: ' + removed.projectName);
      }
      try {
        localStorage.setItem('sequence_diagrams', JSON.stringify(saved));
        this.savedDiagrams = saved;
        this.currentDiagramId = diagramData.id;
        this.$message.success('时序图保存成功');
      } catch (e) {
        this.$message.error('保存失败，可能是存储空间不足');
      }
    },

    // AI 生成
    submitAIGenerate() {
      if (!this.aiForm.projectName.trim()) {
        this.$message.warning('请输入课题名称');
        return;
      }
      if (!this.aiForm.functionDescription.trim()) {
        this.$message.warning('请输入系统业务描述');
        return;
      }

      // 确认消耗积分
      this.$confirm(`生成时序图将消耗 ${this.pointsConfig.sequenceDiagram} 积分，是否继续？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const loading = this.$loading({
          lock: true,
          text: 'AI正在生成时序图，请稍候...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        });
        this.request.post('/ai/generateSequence', {
          projectName: this.aiForm.projectName,
          moduleName: this.aiForm.moduleName || this.aiForm.projectName,
          actors: this.aiForm.actorList.join(','),
          functionDescription: this.aiForm.functionDescription
        }).then(res => {
          loading.close();
          if (res.code === '200') {
            this.renderAIResult(res.data);
            this.$message.success('时序图生成成功！');
            // 自动保存
            this.autoSaveDiagram();
            // 刷新积分
            this.$store.dispatch('user/getInfo');
          } else {
            this.$message.error(res.msg || '生成失败');
          }
        }).catch(error => {
          loading.close();
          this.$message.error('生成失败：' + (error.message || '网络错误'));
        });
      }).catch(() => {});
    },

    // 自动保存（静默保存）
    autoSaveDiagram() {
      if (!this.aiForm.projectName.trim()) return;
      if (!this.aiForm.moduleName.trim()) return;
      if (!this.aiForm.functionDescription.trim()) return;

      const diagramData = {
        id: 'seq_' + Date.now(),
        projectName: this.aiForm.projectName,
        moduleName: this.aiForm.moduleName,
        actors: this.aiForm.actorList.join(','),
        functionDescription: this.aiForm.functionDescription,
        timestamp: Date.now(),
        seqData: JSON.parse(JSON.stringify(this.seqData)),
        svgWidth: this.svgWidth,
        svgHeight: this.svgHeight,
        settings: {
          fontFamily: this.fontFamily,
          fontSize: this.fontSize,
          fontWeight: this.fontWeight,
          fontColor: this.fontColor,
          lineColor: this.lineColor,
          lineWidth: this.lineWidth,
          arrowSize: this.arrowSize
        }
      };
      let saved = this.loadSavedDiagrams();
      saved.push(diagramData);
      if (saved.length > 20) {
        saved.shift();
      }
      try {
        localStorage.setItem('sequence_diagrams', JSON.stringify(saved));
        this.savedDiagrams = saved;
        this.currentDiagramId = diagramData.id;
      } catch (e) {
        console.error('自动保存失败', e);
      }
    },

    // 渲染 AI 返回的时序图数据
    renderAIResult(data) {
      if (!data) return;
      this.seqData = {
        participants: data.participants || [],
        messages: data.messages || [],
        frames: data.frames || []
      };
      if (data.svgWidth)  this.svgWidth  = data.svgWidth;
      if (data.svgHeight) this.svgHeight = data.svgHeight;
      // 重置缩放和平移
      this.zoom = 1;
      this.panX = 0;
      this.panY = 0;
    },

    // localStorage
    loadSavedDiagrams() {
      try {
        const saved = localStorage.getItem('sequence_diagrams');
        return saved ? JSON.parse(saved) : [];
      } catch (e) {
        return [];
      }
    },

    // 历史弹框
    showHistoryDialog() {
      this.showHistoryModal = true;
      this.currentPage = 1;
    },
    closeHistoryDialog() {
      this.showHistoryModal = false;
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    viewFunctionDescription(desc) {
      this.currentDesc = desc;
      this.showDescDialog = true;
    },
    loadDiagramFromHistory(diagram) {
      this.$confirm('加载时序图将覆盖当前内容，是否继续？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.aiForm.projectName = diagram.projectName || '';
        this.aiForm.moduleName = diagram.moduleName || '';
        this.aiForm.actorList = diagram.actors ? diagram.actors.split(',').map(s => s.trim()).filter(Boolean) : [];
        this.aiForm.functionDescription = diagram.functionDescription || '';
        if (diagram.settings) {
          this.fontFamily = diagram.settings.fontFamily || this.fontFamily;
          this.fontSize = diagram.settings.fontSize || this.fontSize;
          this.fontWeight = diagram.settings.fontWeight || this.fontWeight;
          this.fontColor = diagram.settings.fontColor || this.fontColor;
          this.lineColor = diagram.settings.lineColor || this.lineColor;
          this.lineWidth = diagram.settings.lineWidth || this.lineWidth;
          this.arrowSize = diagram.settings.arrowSize || this.arrowSize;
        }
        // 恢复画布数据
        if (diagram.seqData) {
          this.seqData = diagram.seqData;
          this.svgWidth  = diagram.svgWidth  || this.svgWidth;
          this.svgHeight = diagram.svgHeight || this.svgHeight;
          this.zoom = 1;
          this.panX = 0;
          this.panY = 0;
        }
        this.currentDiagramId = diagram.id;
        this.showHistoryModal = false;
        this.$message.success('时序图加载成功');
      }).catch(() => {});
    },
    deleteDiagramFromHistory(index) {
      const diagram = this.savedDiagrams[index];
      this.$confirm('确定要删除时序图 "' + diagram.projectName + '" 吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.savedDiagrams.splice(index, 1);
        try {
          localStorage.setItem('sequence_diagrams', JSON.stringify(this.savedDiagrams));
          this.$message.success('时序图已删除');
          if (this.currentDiagramId === diagram.id) this.currentDiagramId = null;
        } catch (e) {
          this.$message.error('删除失败');
        }
      }).catch(() => {});
    },
    getOriginalIndex(diagram) {
      return this.savedDiagrams.findIndex(d => d.id === diagram.id);
    },
    getRowClassName({ row }) {
      return this.currentDiagramId === row.id ? 'current-sequence-row' : '';
    },
    formatAbsoluteTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }
  },
  mounted() {
    this.increasePageView();
    this.buildDefaultSeqData();
    this.loadPointsConfig();
    this.savedDiagrams = this.loadSavedDiagrams();
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.onFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.onFullscreenChange);

    // 默认加载最近一个历史记录
    this.$nextTick(() => {
      if (this.savedDiagrams.length > 0) {
        const latest = this.savedDiagrams[this.savedDiagrams.length - 1];
        this.aiForm.projectName = latest.projectName || '';
        this.aiForm.moduleName = latest.moduleName || '';
        this.aiForm.actorList = latest.actors ? latest.actors.split(',').map(s => s.trim()).filter(Boolean) : [];
        this.aiForm.functionDescription = latest.functionDescription || '';
        this.currentDiagramId = latest.id;
        if (latest.settings) {
          this.fontFamily = latest.settings.fontFamily || this.fontFamily;
          this.fontSize = latest.settings.fontSize || this.fontSize;
          this.fontWeight = latest.settings.fontWeight || this.fontWeight;
          this.fontColor = latest.settings.fontColor || this.fontColor;
          this.lineColor = latest.settings.lineColor || this.lineColor;
          this.lineWidth = latest.settings.lineWidth || this.lineWidth;
          this.arrowSize = latest.settings.arrowSize || this.arrowSize;
        }
        if (latest.seqData) {
          this.seqData = latest.seqData;
          this.svgWidth  = latest.svgWidth  || this.svgWidth;
          this.svgHeight = latest.svgHeight || this.svgHeight;
        }
      }
    });
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.onFullscreenChange);
  }
}
</script>

<style scoped>
.sequence-editor {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 118px);
  overflow: hidden;
}

.editor-content {
  display: flex;
  height: calc(100vh - 118px);
  overflow: hidden;
}

.element-library {
  width: 380px;
  background: #fff;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 1;
  border-right: 1px solid #d1d5db;
}

.panel-header {
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.panel-icon {
  width: 22px;
  height: 22px;
  color: #4f46e5;
}

.btn-history {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  color: #4f46e5;
  background: #fff;
  border: 1px solid #4f46e5;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-history:hover {
  background: #4f46e5;
  color: #fff;
}

.btn-history svg {
  width: 16px;
  height: 16px;
}

.save-reminder {
  font-size: 14px;
  color: #856404;
  background: #fff3cd;
  padding: 6px 14px;
  border-radius: 4px;
  margin: 12px 16px 0;
  flex-shrink: 0;
}

.ai-form-panel {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.ai-form-panel .el-form {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.ai-form-panel .el-form-item:last-child {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.ai-form-panel .el-form-item:last-child .el-form-item__content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.func-editor {
  flex: 1;
  width: 100%;
  background: #ffffff;
  color: #333;
  font-family: inherit;
  font-size: 13px;
  padding: 16px;
  resize: none;
  border: 1px solid #dcdfe6;
  outline: none;
  line-height: 1.6;
  border-radius: 4px;
  box-sizing: border-box;
  min-height: 0;
  height: calc(100vh - 730px);
}

.ai-form-panel .el-form-item__label {
  font-size: 15px;
  font-weight: 500;
  color: #333;
}

.panel-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}

.btn-primary, .btn-ai {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  border: none;
  transition: background 0.2s;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-points {
  font-size: 16px;
  font-weight: 600;
  opacity: 0.9;
  white-space: nowrap;
}

.btn-primary {
  background: #4f46e5;
  color: #fff;
}

.btn-primary:hover {
  background: #4338ca;
}

.btn-ai {
  background: #10b981;
  color: #fff;
}

.btn-ai:hover {
  background: #059669;
}

.btn-primary svg {
  width: 22px;
  height: 22px;
}

.btn-ai .ai-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.actor-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-height: 32px;
  padding: 4px 0;
}

/* 右侧区域 */
.canvas-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9fafb;
  margin-left: 10px;
  border-left: 1px solid #d1d5db;

}

/* 全屏状态下撑满屏幕 */
.canvas-area:-webkit-full-screen { width: 100vw; height: 100vh; margin: 0; }
.canvas-area:-moz-full-screen    { width: 100vw; height: 100vh; margin: 0; }
.canvas-area:-ms-fullscreen      { width: 100vw; height: 100vh; margin: 0; }
.canvas-area:fullscreen          { width: 100vw; height: 100vh; margin: 0; }

.tabs-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.tabs {
  display: flex;
  gap: 24px;
}

.tab {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 0;
  font-size: 16px;
  font-weight: 400;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.tab svg {
  width: 22px;
  height: 22px;
}

.tab.active {
  color: #4f46e5;
}

.tab.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: #4f46e5;
}

.canvas-actions {
  display: flex;
  gap: 10px;
}

.tool-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.tool-btn i {
  font-size: 14px;
}

.btn-green { background: #10b981; }
.btn-green:hover { background: #059669; }
.btn-teal { background: #0891b2; }
.btn-teal:hover { background: #0e7490; }
.btn-purple { background: #8c6ff7; }
.btn-purple:hover { background: #7559d4; }
.btn-orange { background: #f59e0b; }
.btn-orange:hover { background: #d97706; }
.btn-blue { background: #3b82f6; }
.btn-blue:hover { background: #2563eb; }

.toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 24px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.tip-banner {
  font-size: 14px;
  color: #856404;
  background: #fff3cd;
  padding: 6px 14px;
  border-radius: 4px;
}

.params-setting {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 16px 24px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.param-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.param-item label {
  font-size: 14px;
  color: #374151;
  white-space: nowrap;
  font-weight: 500;
}

.number-input {
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: white;
  padding: 0 4px;
  height: 32px;
}

.number-input .el-button {
  width: 20px;
  height: 20px;
  padding: 0;
  min-height: auto;
  border: none;
  background: transparent;
  color: #606266;
  font-size: 14px;
}

.number-input .el-button:hover {
  background: #f5f7fa;
  color: #409eff;
}

.number-value {
  min-width: 30px;
  text-align: center;
  font-size: 13px;
  color: #606266;
  line-height: 32px;
}

.canvas-content {
  flex: 1;
  overflow: hidden;
  background: white;
  cursor: grab;
  position: relative;
}

.canvas-content:active {
  cursor: grabbing;
}

.seq-svg {
  display: block;
  user-select: none;
}

/* 历史弹框 */
.history-content {
  min-height: 400px;
}

.history-description {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 6px;
}

.history-description ul {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.history-description li {
  margin: 4px 0;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

.diagram-name.current {
  color: #52c41a;
}

.diagram-name.current::after {
  content: " (当前)";
  font-size: 11px;
  color: #52c41a;
  font-weight: normal;
}

.absolute-time {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 16px 0;
}

.el-table /deep/ .current-sequence-row {
  background-color: #f6ffed !important;
}

.el-table /deep/ .current-sequence-row:hover td {
  background-color: #f6ffed !important;
}
</style>
